import React, { useState } from 'react';
import {
    Box, Paper, Typography, Collapse, Button, Divider,
    TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, Title, Tooltip, Legend, Filler,
);

// ---------------------------------------------------------------------------
// Acceptance helpers
// ---------------------------------------------------------------------------

function acceptanceColor(status) {
    const map = {
        acceptable:   '#81c784',
        marginal:     '#ffb74d',
        unacceptable: '#e57373',
    };
    return map[status] || '#90caf9';
}

function AcceptanceIcon({ status, sx }) {
    const Icon = {
        acceptable:   CheckCircleIcon,
        marginal:     WarningIcon,
        unacceptable: ErrorIcon,
    }[status];
    if (!Icon) return null;
    return <Icon sx={{ color: acceptanceColor(status), fontSize: '1rem', ...sx }} />;
}

function StatusLabel({ status }) {
    const labels = {
        acceptable:   'ACCEPTABLE',
        marginal:     'MARGINAL',
        unacceptable: 'UNACCEPTABLE',
    };
    return labels[status] || status?.toUpperCase() || '—';
}

// ---------------------------------------------------------------------------
// Metric row
// ---------------------------------------------------------------------------

function MetricRow({ label, value, unit, acceptance, threshold, secondary }) {
    const color = acceptance ? acceptanceColor(acceptance) : 'rgba(255,255,255,0.85)';
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color, fontSize: '0.9rem' }}>
                        {value}{unit && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.5)', marginLeft: 4 }}>{unit}</span>}
                    </Typography>
                    {acceptance && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AcceptanceIcon status={acceptance} />
                            <Typography variant="caption" sx={{ color: acceptanceColor(acceptance), fontWeight: 600, fontSize: '0.72rem' }}>
                                <StatusLabel status={acceptance} />
                            </Typography>
                            {threshold && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}>
                                    {threshold}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                    {label}
                </Typography>
                {secondary && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', display: 'block' }}>
                        {secondary}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Context block for reference confidence
// ---------------------------------------------------------------------------

function ReferenceContextBlock({ referenceConfidence }) {
    const blocks = {
        prior_average: {
            color: '#ffb74d',
            text: 'Reference was established from prior measurements. This value carries its own measurement uncertainty and may not represent the true reference. Bias results are shown but should be interpreted cautiously.',
        },
        drawing_nominal: {
            color: '#ff8a65',
            text: 'Using a process target or drawing nominal as reference conflates measurement error with product conformance. A true calibration standard is required for meaningful bias evaluation.',
        },
        not_established: {
            color: 'rgba(255,255,255,0.4)',
            text: 'Reference value not established — bias cannot be evaluated for this study. Consider establishing a certified reference before interpreting gauge performance.',
        },
    };
    const block = blocks[referenceConfidence];
    if (!block) return null;
    return (
        <Box sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${block.color}44`, backgroundColor: `${block.color}0d`, mt: 1 }}>
            <Typography variant="caption" sx={{ color: block.color, lineHeight: 1.5, display: 'block', fontSize: '0.77rem' }}>
                {block.text}
            </Typography>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

const chartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        title: { display: true, text: title, color: 'rgba(255,255,255,0.7)', font: { size: 12 } },
    },
    scales: {
        x: { ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.06)' } },
        y: { ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.06)' } },
    },
});

function RunChartPlot({ runChart }) {
    const { values, mean, ucl, lcl } = runChart;
    const labels = values.map((_, i) => i + 1);
    const data = {
        labels,
        datasets: [
            {
                label: 'Measurement',
                data: values,
                borderColor: '#90caf9',
                backgroundColor: 'rgba(144,202,249,0.12)',
                pointRadius: 3,
                borderWidth: 1.5,
                tension: 0,
            },
            { label: 'Mean',  data: Array(values.length).fill(mean), borderColor: '#81c784', borderWidth: 1, borderDash: [4,3], pointRadius: 0 },
            { label: 'UCL',   data: Array(values.length).fill(ucl),  borderColor: '#e57373', borderWidth: 1, borderDash: [2,2], pointRadius: 0 },
            { label: 'LCL',   data: Array(values.length).fill(lcl),  borderColor: '#e57373', borderWidth: 1, borderDash: [2,2], pointRadius: 0 },
        ],
    };
    return (
        <Box sx={{ height: 200 }}>
            <Line data={data} options={chartOptions('Run Chart')} />
        </Box>
    );
}

function HistogramPlot({ histogram }) {
    const { bins, counts } = histogram;
    const labels = bins.map(b => b.toFixed(4));
    const data = {
        labels,
        datasets: [{
            label: 'Count',
            data: counts,
            backgroundColor: 'rgba(144,202,249,0.5)',
            borderColor: 'rgba(144,202,249,0.9)',
            borderWidth: 1,
        }],
    };
    const opts = {
        ...chartOptions('Histogram'),
        scales: {
            ...chartOptions('').scales,
            x: { ...chartOptions('').scales.x, ticks: { ...chartOptions('').scales.x.ticks, maxRotation: 45, minRotation: 45 } },
        },
    };
    return (
        <Box sx={{ height: 200 }}>
            <Bar data={data} options={opts} />
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Study feedback record
// ---------------------------------------------------------------------------

const CONCLUSION_OPTIONS = [
    { value: 'accepted_production',   label: 'Accepted — proceeding to production / next stage' },
    { value: 'accepted_type2',        label: 'Accepted — proceeding to Type 2 GRR' },
    { value: 'marginal_risk_note',    label: 'Marginal — accepted with risk note' },
    { value: 'rejected_recalibrate',  label: 'Rejected — recalibration required' },
    { value: 'rejected_replaced',     label: 'Rejected — instrument replaced' },
    { value: 'under_investigation',   label: 'Under investigation' },
];

const selectSx = {
    color: 'rgba(255,255,255,0.9)',
    '& .MuiOutlinedInput-notchedOutline':        { borderColor: 'rgba(255,255,255,0.23)' },
    '&:hover .MuiOutlinedInput-notchedOutline':  { borderColor: 'rgba(255,255,255,0.5)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(144,202,249,0.7)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255,255,255,0.9)',
        '& fieldset':             { borderColor: 'rgba(255,255,255,0.23)' },
        '&:hover fieldset':       { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144,202,249,0.7)' },
    },
    '& .MuiInputLabel-root':            { color: 'rgba(255,255,255,0.6)' },
    '& .MuiInputLabel-root.Mui-focused':{ color: 'rgba(144,202,249,0.9)' },
};

function StudyFeedbackRecord({ studyId, onSave }) {
    const [conclusion, setConclusion] = useState('');
    const [notes, setNotes]           = useState('');
    const [saved, setSaved]           = useState(false);

    const handleSave = () => {
        if (onSave) onSave({ studyId, conclusion, notes });
        setSaved(true);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Study Record
            </Typography>

            <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-focused': { color: 'rgba(144,202,249,0.9)' } }}>
                    Conclusion / Action
                </InputLabel>
                <Select
                    value={conclusion}
                    label="Conclusion / Action"
                    onChange={(e) => { setConclusion(e.target.value); setSaved(false); }}
                    sx={selectSx}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                backgroundColor: '#1e242c',
                                border: '1px solid rgba(255,255,255,0.12)',
                                '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' },
                                '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(144,202,249,0.08)' },
                                '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(144,202,249,0.12)' },
                            },
                        },
                    }}
                >
                    {CONCLUSION_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                label="Notes (optional)"
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setSaved(false); }}
                multiline
                rows={2}
                size="small"
                fullWidth
                sx={inputSx}
                inputProps={{ maxLength: 1000 }}
            />

            <Button
                variant={saved ? 'outlined' : 'contained'}
                color="primary"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!conclusion}
                sx={{ alignSelf: 'flex-start' }}
            >
                {saved ? 'Saved' : 'Save record'}
            </Button>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main results component
// ---------------------------------------------------------------------------

function Type1GRRResults({ results, description, createdAt, studyId, onSaveFeedback }) {
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [chartsOpen,   setChartsOpen]   = useState(false);

    if (!results) return null;

    const {
        n, mean, sigma, repeatability6sigma, repeatabilityPctTol, repeatabilityAcceptance,
        bias, biasPctTol, biasAcceptance, cg, cgk, cgAcceptance, cgkAcceptance,
        overallAcceptance, referenceConfidence, histogram, runChart,
    } = results;

    const overallColor = acceptanceColor(overallAcceptance);
    const showBias     = bias !== null && referenceConfidence !== 'not_established';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    {description}
                </Typography>
                {createdAt && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {new Date(createdAt).toLocaleString()}
                    </Typography>
                )}
            </Box>

            {/* Verdict chip */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${overallColor}44`,
                    backgroundColor: `${overallColor}0f`,
                }}
            >
                <AcceptanceIcon status={overallAcceptance} sx={{ fontSize: '1.4rem !important' }} />
                <Typography variant="h6" sx={{ color: overallColor, fontWeight: 700, letterSpacing: '0.04em', fontSize: '1.05rem' }}>
                    <StatusLabel status={overallAcceptance} />
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', ml: 'auto' }}>
                    n = {n}
                </Typography>
            </Box>

            {/* Primary metrics */}
            <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                    Primary Metrics
                </Typography>

                <MetricRow
                    label="Repeatability (6σ)"
                    value={`${repeatabilityPctTol.toFixed(1)}%`}
                    unit="of tolerance"
                    acceptance={repeatabilityAcceptance}
                    threshold="(≤ 15%)"
                    secondary={`6σ = ${repeatability6sigma.toFixed(4)}   |   σ = ${sigma.toFixed(4)}`}
                />

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.75 }} />

                {showBias ? (
                    <MetricRow
                        label="Bias"
                        value={`${biasPctTol.toFixed(1)}%`}
                        unit="of tolerance"
                        acceptance={biasAcceptance}
                        threshold="(≤ 5% acceptable, ≤ 10% marginal)"
                        secondary={`Mean = ${mean.toFixed(4)}   |   Reference: shown in form`}
                    />
                ) : (
                    <Box sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>
                            Bias — <em>not evaluated</em>
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                            {referenceConfidence === 'not_established'
                                ? 'Reference not established for this study.'
                                : `Mean = ${mean.toFixed(4)}`}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Reference confidence context block */}
            <ReferenceContextBlock referenceConfidence={referenceConfidence} />

            {/* Advanced metrics */}
            <Box>
                <Button
                    size="small"
                    onClick={() => setAdvancedOpen(v => !v)}
                    endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', px: 0, '&:hover': { color: '#90caf9' } }}
                >
                    Advanced metrics
                </Button>
                <Collapse in={advancedOpen}>
                    <Paper sx={{ p: 2, mt: 0.75, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        {cg !== null ? (
                            <>
                                <MetricRow
                                    label="Cg"
                                    value={cg.toFixed(2)}
                                    acceptance={cgAcceptance}
                                    threshold="(≥ 1.33)"
                                />
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.75 }} />
                                <MetricRow
                                    label="Cgk"
                                    value={cgk.toFixed(2)}
                                    acceptance={cgkAcceptance}
                                    threshold="(≥ 1.33)"
                                    secondary="Combines bias + repeatability. Hidden from primary view per design."
                                />
                            </>
                        ) : (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                                Cg and Cgk require a reference value to be established.
                            </Typography>
                        )}
                    </Paper>
                </Collapse>
            </Box>

            {/* Full analysis (charts) */}
            <Box>
                <Button
                    size="small"
                    onClick={() => setChartsOpen(v => !v)}
                    endIcon={chartsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', px: 0, '&:hover': { color: '#90caf9' } }}
                >
                    Full analysis
                </Button>
                <Collapse in={chartsOpen}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {histogram && (
                            <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                <HistogramPlot histogram={histogram} />
                            </Paper>
                        )}
                        {runChart && (
                            <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                <RunChartPlot runChart={runChart} />
                            </Paper>
                        )}
                    </Box>
                </Collapse>
            </Box>

            {/* Study feedback record */}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
            <StudyFeedbackRecord studyId={studyId} onSave={onSaveFeedback} />
        </Box>
    );
}

export default Type1GRRResults;
